// Feature 009: Deal Operating System
// Opportunity card component for pipeline view
// Design: Black (#000), Bone (#fffbf7), Sage (#a1c6a2)
import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import BlockerBadge from './BlockerBadge';

const OpportunityCard = ({ opportunity, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: opportunity.id.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isStale = opportunity.stale || isOpportunityStale(opportunity);

  // Check for incomplete data warnings
  const hasWarnings = !opportunity.counterparty_id || !opportunity.category;

  const getCategoryColor = (category) => {
    const colors = {
      sports: 'bg-[rgba(161,198,162,0.2)] text-[#a1c6a2]',
      music: 'bg-[rgba(180,160,200,0.2)] text-[#b4a0c8]',
      hospitality: 'bg-[rgba(212,165,116,0.2)] text-[#d4a574]',
      creator: 'bg-[rgba(200,160,180,0.2)] text-[#c8a0b4]',
      other: 'bg-[rgba(255,251,247,0.1)] text-[rgba(255,251,247,0.6)]',
    };
    return colors[category?.toLowerCase()] || colors.other;
  };

  const getChannelIcon = (channel) => {
    switch (channel?.toLowerCase()) {
      case 'ghost':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        );
      case 'dtc':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
          </svg>
        );
      case 'event':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      default:
        return null;
    }
  };

  // Handle click on the card - navigate unless we're dragging
  const handleCardClick = (e) => {
    // Only navigate if this was a click, not a drag release
    if (!transform && e.target.tagName !== 'A') {
      window.location.href = `/opportunities/${opportunity.id}`;
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleCardClick}
      className={`relative bg-black border border-[rgba(255,251,247,0.1)] p-3 cursor-grab active:cursor-grabbing hover:border-[rgba(255,251,247,0.3)] transition-colors select-none ${
        isDragging ? 'opacity-50 border-[#a1c6a2]' : ''
      } ${isStale ? 'border-l-2 border-l-[#d4a574]' : ''}`}
    >
      {/* Warning indicator for incomplete data */}
      {hasWarnings && (
        <span
          className="absolute top-2 right-2 w-2 h-2 bg-[#f0ad4e] rounded-full"
          title="Some fields are incomplete"
        />
      )}
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <a
          href={`/opportunities/${opportunity.id}`}
          onClick={(e) => {
            // Prevent navigation if we're in the middle of a drag
            if (transform) {
              e.preventDefault();
            }
          }}
          className="text-sm font-medium text-[#fffbf7] hover:text-[#a1c6a2] truncate flex-1 transition-colors"
        >
          {opportunity.name}
        </a>
        {isStale && (
          <span className="ml-2 flex-shrink-0" title="Stale - no activity for 7+ days">
            <svg className="w-4 h-4 text-[#d4a574]" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </span>
        )}
      </div>

      {/* Counterparty */}
      {opportunity.counterparty_name && (
        <p className="text-xs text-[rgba(255,251,247,0.5)] truncate mb-2">
          {opportunity.counterparty_name}
        </p>
      )}

      {/* Meta info */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Category Badge */}
        <span
          className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium tracking-wider uppercase ${getCategoryColor(opportunity.category)}`}
          style={{ fontFamily: "'GT Pressura Mono', monospace" }}
        >
          {opportunity.category}
        </span>

        {/* Channel Icon */}
        {opportunity.primary_channel && (
          <span className="text-[rgba(255,251,247,0.4)]" title={`Channel: ${opportunity.primary_channel}`}>
            {getChannelIcon(opportunity.primary_channel)}
          </span>
        )}

        {/* Document Count */}
        {opportunity.documents_count > 0 && (
          <span className="inline-flex items-center text-xs text-[rgba(255,251,247,0.4)]">
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {opportunity.documents_count}
          </span>
        )}
      </div>

      {/* Blocker Badge */}
      {opportunity.blocking_issue && (
        <BlockerBadge issue={opportunity.blocking_issue} />
      )}
    </div>
  );
};

// Helper to check if opportunity is stale (7+ days without activity)
function isOpportunityStale(opportunity) {
  const lastActivity = opportunity.last_activity_at || opportunity.updated_at;
  if (!lastActivity) return false;

  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  return new Date(lastActivity) < sevenDaysAgo;
}

export default OpportunityCard;
